import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Alert, Snackbar } from '@mui/material';

import {
  useFlightSearchFilterOptionsMutation,
  useFlightSearchMutation,
  useHotelSearchMutation,
  usePlaceSearchMutation,
  useGetPlacesTypesQuery,
} from '../../../store/services/ideasApi';
import { useIsMobile } from '../../../hooks/useWindowWidth';
import { useBottomSuccessToast } from '../../../hooks/useBottomSuccessToast';
import {
  FlightSearchFormData,
  FlightFilters as FlightFiltersType,
  FLIGHT_FILTERS_INITIAL_VALUES,
  FlightSearchPayload,
  FlightItem,
  AirlineOption,
  AirportOption,
  TravelClass,
  normalizeFlightFilters,
} from '../../../types/flight.types';
import {
  HotelItem,
  HotelFilters as HotelFiltersType,
  HOTEL_FILTERS_INITIAL_VALUES,
  HotelSearchContextForSave,
  HotelSearchPayload,
} from '../../../types/hotel.types';
import {
  PlaceItem,
  PlaceFilters as PlaceFiltersType,
  PLACE_FILTERS_INITIAL_VALUES,
  PlaceSearchPayload,
  PlaceSearchContextForSave,
  normalizePlaceFilters,
} from '../../../types/place.types';
import { FlightSearchContextForSave } from '../../../utils/flightToIdeaPayload';
import { hasBackendFlightFiltersChanged } from '../../../utils/flightSearchQueryParams';
import { hasBackendPlaceFiltersChanged } from '../../../utils/placeSearchQueryParams';
import { PlacesSearchFormData } from './types/placesSearch.types';
import {
  applyPlaceBooleanFilters,
  normalizePlaceSearchResponse,
  radiusMetersFromDistanceField,
  resolvePlaceTypeLabel,
  resolveRankLabel,
  selectablePlaceTypeOptions,
} from './utils/placesSearch.utils';
import type { PlacesFormSort } from './types/placesSearch.types';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { getFlightItemKey } from '../../../utils/flightItemKey';
import { getTotalPages } from '../../../utils/pagination.utils';

import styles from './Discover.module.scss';
import FlightSearchForm from './components/FlightSearchForm/FlightSearchForm';
import FlightFilters from './components/FlightFilters/FlightFilters';
import PlaceFilters from './components/PlaceFilters/PlaceFilters';
import PlaceFiltersModal from './components/PlaceFiltersModal/PlaceFiltersModal';
import PlaceResultCard from './components/PlaceResultCard/PlaceResultCard';
import PlacesSearchForm from './components/PlacesSearchForm/PlacesSearchForm';
import DiscoverSearchPanel from './components/DiscoverSearchPanel/DiscoverSearchPanel';
import DiscoverTab from './components/DiscoverTab/DiscoverTab';
import FlightResultCard from './components/FlightResultCard/FlightResultCard';
import FlightFiltersModal from './components/FlightFiltersModal/FlightFiltersModal';
import SaveFlightModal from './components/SaveFlightModal/SaveFlightModal';
import SaveHotelModal from './components/SaveHotelModal/SaveHotelModal';
import SavePlaceModal from './components/SavePlaceModal/SavePlaceModal';
import DiscoverPagination from './components/DiscoverPagination/DiscoverPagination';
import HotelSearchForm from './components/HotelSearchForm/HotelSearchForm';
import HotelFilters from './components/HotelFilters/HotelFilters';
import HotelResultCard from './components/HotelResultCard/HotelResultCard';
import HotelFiltersModal from './components/HotelFiltersModal/HotelFiltersModal';
import { HotelSearchFormData } from './types/hotelSearch.types';

type TabValue = 'flights' | 'hotels' | 'places' | 'discover';

type LastFlightSearchMeta = {
  context: FlightSearchContextForSave;
  travelersCount: number;
  travelClass: TravelClass;
};

const Discover: FC = () => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<TabValue>('flights');
  const activeTabRef = useRef<TabValue>(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const [searchResults, setSearchResults] = useState<FlightItem[]>([]);
  const [filters, setFilters] = useState<FlightFiltersType>(FLIGHT_FILTERS_INITIAL_VALUES);
  const [airlineOptions, setAirlineOptions] = useState<AirlineOption[]>([]);
  const [airportOptions, setAirportOptions] = useState<AirportOption[]>([]);
  const [hasSearchedFlights, setHasSearchedFlights] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [lastSearchMeta, setLastSearchMeta] = useState<LastFlightSearchMeta | null>(null);
  const [flightPage, setFlightPage] = useState(1);
  const [flightTotalCount, setFlightTotalCount] = useState(0);
  const [lastFlightPayload, setLastFlightPayload] = useState<FlightSearchPayload | null>(null);
  const lastFlightPayloadRef = useRef<FlightSearchPayload | null>(null);
  const appliedFlightFiltersRef = useRef<FlightFiltersType>(FLIGHT_FILTERS_INITIAL_VALUES);
  useEffect(() => {
    lastFlightPayloadRef.current = lastFlightPayload;
  }, [lastFlightPayload]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [flightToSave, setFlightToSave] = useState<FlightItem | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [hotelSearchResults, setHotelSearchResults] = useState<HotelItem[]>([]);
  const [hotelFilters, setHotelFilters] = useState<HotelFiltersType>(HOTEL_FILTERS_INITIAL_VALUES);
  const [hasSearchedHotels, setHasSearchedHotels] = useState(false);
  const [hotelSearchError, setHotelSearchError] = useState<string | null>(null);
  const [lastHotelSearchContext, setLastHotelSearchContext] = useState<HotelSearchContextForSave | null>(null);
  const [hotelPage, setHotelPage] = useState(1);
  const [hotelTotalCount, setHotelTotalCount] = useState(0);
  const [lastHotelPayload, setLastHotelPayload] = useState<HotelSearchPayload | null>(null);
  const [hotelFilteredResults, setHotelFilteredResults] = useState<HotelItem[]>([]);
  const [saveHotelModalOpen, setSaveHotelModalOpen] = useState(false);
  const [hotelToSave, setHotelToSave] = useState<HotelItem | null>(null);

  const [placeSearchResults, setPlaceSearchResults] = useState<PlaceItem[]>([]);
  const [placeFilters, setPlaceFilters] = useState<PlaceFiltersType>(PLACE_FILTERS_INITIAL_VALUES);
  const [hasSearchedPlaces, setHasSearchedPlaces] = useState(false);
  const [placeSearchError, setPlaceSearchError] = useState<string | null>(null);
  const [lastPlaceSearchContext, setLastPlaceSearchContext] = useState<PlaceSearchContextForSave | null>(null);
  const [placeCategoryLabel, setPlaceCategoryLabel] = useState('');
  const [placePage, setPlacePage] = useState(1);
  const [placeTotalCount, setPlaceTotalCount] = useState(0);
  const [lastPlacePayload, setLastPlacePayload] = useState<PlaceSearchPayload | null>(null);
  const lastPlacePayloadRef = useRef<PlaceSearchPayload | null>(null);
  const lastPlaceSortRef = useRef<PlacesFormSort>('rating:asc');
  const appliedPlaceFiltersRef = useRef<PlaceFiltersType>(PLACE_FILTERS_INITIAL_VALUES);
  useEffect(() => {
    lastPlacePayloadRef.current = lastPlacePayload;
  }, [lastPlacePayload]);
  const [savePlaceModalOpen, setSavePlaceModalOpen] = useState(false);
  const [placeToSave, setPlaceToSave] = useState<PlaceItem | null>(null);

  const {
    show: showSaveSuccessToast,
    onClose: closeSaveSuccessToast,
    snackbarKey: saveSuccessSnackbarKey,
    open: saveSuccessToastOpen,
    message: saveSuccessToastMessage,
  } = useBottomSuccessToast();

  const [flightSearch] = useFlightSearchMutation();
  const [flightSearchFilterOptions] = useFlightSearchFilterOptionsMutation();
  const [isFlightSearching, setIsFlightSearching] = useState(false);
  const [hotelSearch, { isLoading: isHotelLoading }] = useHotelSearchMutation();
  const [placeSearch, { isLoading: isPlaceLoading }] = usePlaceSearchMutation();

  const { data: placeTypesFromApi = [] } = useGetPlacesTypesQuery(undefined, {
    skip: activeTab !== 'places',
  });

  const placeTypeOptionsForLabel = useMemo(() => selectablePlaceTypeOptions(placeTypesFromApi), [placeTypesFromApi]);

  const resetAllDiscoverSearchState = () => {
    setSearchResults([]);
    setAirlineOptions([]);
    setAirportOptions([]);
    setHasSearchedFlights(false);
    setSearchError(null);
    setLastSearchMeta(null);
    setFlightPage(1);
    setFlightTotalCount(0);
    setLastFlightPayload(null);
    lastFlightPayloadRef.current = null;
    setFilters(FLIGHT_FILTERS_INITIAL_VALUES);
    appliedFlightFiltersRef.current = FLIGHT_FILTERS_INITIAL_VALUES;
    setSaveModalOpen(false);
    setFlightToSave(null);

    setHotelSearchResults([]);
    setHasSearchedHotels(false);
    setHotelSearchError(null);
    setLastHotelSearchContext(null);
    setHotelPage(1);
    setHotelTotalCount(0);
    setLastHotelPayload(null);
    setHotelFilters(HOTEL_FILTERS_INITIAL_VALUES);
    setSaveHotelModalOpen(false);
    setHotelToSave(null);

    setPlaceSearchResults([]);
    setHasSearchedPlaces(false);
    setPlaceSearchError(null);
    setLastPlaceSearchContext(null);
    setPlaceCategoryLabel('');
    setPlacePage(1);
    setPlaceTotalCount(0);
    setLastPlacePayload(null);
    setPlaceFilters(PLACE_FILTERS_INITIAL_VALUES);
    appliedPlaceFiltersRef.current = PLACE_FILTERS_INITIAL_VALUES;
    lastPlaceSortRef.current = 'rating:asc';
    setSavePlaceModalOpen(false);
    setPlaceToSave(null);
  };

  const handleTabChange = (event: React.MouseEvent<HTMLElement>, newValue: TabValue | null) => {
    if (newValue === null) return;
    if (newValue === activeTab) return;
    activeTabRef.current = newValue;
    setActiveTab(newValue);
    resetAllDiscoverSearchState();
  };

  const runFlightSearch = async (
    payload: FlightSearchPayload,
    page: number,
    options?: {
      formValues?: FlightSearchFormData;
      searchFilters?: FlightFiltersType;
      isNewSearch?: boolean;
    }
  ) => {
    const tabWhenStarted = activeTabRef.current;
    const searchFilters = normalizeFlightFilters(options?.searchFilters ?? filters);
    setSearchError(null);
    closeSaveSuccessToast();
    setIsFlightSearching(true);
    if (options?.isNewSearch) {
      setSearchResults([]);
      setFlightTotalCount(0);
      setFlightPage(1);
    }
    try {
      if (options?.isNewSearch) {
        try {
          const filterOptions = await flightSearchFilterOptions(payload).unwrap();
          if (activeTabRef.current !== tabWhenStarted) return;
          setAirlineOptions(filterOptions.airlines ?? []);
          setAirportOptions(filterOptions.airports ?? []);
        } catch (filterError) {
          console.error('Flight filter options error:', filterError);
          if (activeTabRef.current !== tabWhenStarted) return;
          setAirlineOptions([]);
          setAirportOptions([]);
        }
      }

      const result = await flightSearch({ payload, page, searchFilters }).unwrap();

      if (activeTabRef.current !== tabWhenStarted) return;

      const responseCurrency = typeof result.currency === 'string' ? result.currency.trim() : '';
      const flightItems = (result.flight_items ?? []).map(item => ({
        ...item,
        currency: item.currency?.trim() || responseCurrency || item.currency,
      }));
      setSearchResults(flightItems);
      const flightCount = result.count ?? flightItems.length;
      setFlightTotalCount(flightCount);
      setFlightPage(page);
      setLastFlightPayload(payload);
      lastFlightPayloadRef.current = payload;
      appliedFlightFiltersRef.current = searchFilters;
      if (options?.isNewSearch) {
        setFilters(FLIGHT_FILTERS_INITIAL_VALUES);
        appliedFlightFiltersRef.current = FLIGHT_FILTERS_INITIAL_VALUES;
      }
      setHasSearchedFlights(true);
      setSaveModalOpen(false);
      setFlightToSave(null);
      if (options?.formValues) {
        const formValues = options.formValues;
        setLastSearchMeta({
          context: {
            flightFrom: formValues.flightFrom,
            flightTo: formValues.flightTo,
            departDate: formValues.departDate,
            returnDate: formValues.returnDate?.trim() ? formValues.returnDate : undefined,
          },
          travelersCount: formValues.travelersCount,
          travelClass: formValues.travelClass,
        });
      }
    } catch (error: any) {
      console.error('Flight search error:', error);
      if (activeTabRef.current !== tabWhenStarted) return;
      const isInitialSearch = Boolean(options?.isNewSearch);
      if (isInitialSearch) {
        setSearchResults([]);
        setAirlineOptions([]);
        setAirportOptions([]);
        setFlightTotalCount(0);
        setFlightPage(1);
        setLastFlightPayload(null);
        lastFlightPayloadRef.current = null;
        setLastSearchMeta(null);
      }
      setHasSearchedFlights(true);
      setSearchError(getApiErrorMessage(error, 'Flight search failed. Please try again.', { firstOnly: true }));
    } finally {
      if (activeTabRef.current === tabWhenStarted) {
        setIsFlightSearching(false);
      }
    }
  };

  const handleSearchSubmit = async (formValues: FlightSearchFormData) => {
    const payload: FlightSearchPayload = {
      flight_from: formValues.flightFrom,
      flight_to: formValues.flightTo,
      depart_date: formValues.departDate,
      return_date: formValues.returnDate || undefined,
      travelers_count: formValues.travelersCount,
      travel_class: formValues.travelClass,
    };
    await runFlightSearch(payload, 1, {
      formValues,
      searchFilters: FLIGHT_FILTERS_INITIAL_VALUES,
      isNewSearch: true,
    });
  };

  const handleFlightFiltersChange = (next: FlightFiltersType) => {
    const normalized = normalizeFlightFilters(next);
    const payload = lastFlightPayloadRef.current;
    const shouldRefetch =
      hasSearchedFlights &&
      payload != null &&
      hasBackendFlightFiltersChanged(appliedFlightFiltersRef.current, normalized);

    setFilters(normalized);

    if (!shouldRefetch) return;

    appliedFlightFiltersRef.current = normalized;
    void runFlightSearch(payload, 1, { searchFilters: normalized });
  };

  const handleFlightPageChange = (page: number) => {
    if (!lastFlightPayload || page === flightPage) return;
    void runFlightSearch(lastFlightPayload, page, { searchFilters: filters });
  };

  const runHotelSearch = async (payload: HotelSearchPayload, page: number, formValues?: HotelSearchFormData) => {
    const tabWhenStarted = activeTabRef.current;
    setHotelSearchError(null);
    closeSaveSuccessToast();
    try {
      const result = await hotelSearch({ payload, page }).unwrap();
      const items = result.results ?? [];

      if (activeTabRef.current !== tabWhenStarted) return;

      setHotelSearchResults(items);
      const hotelCount = result.count ?? items.length;
      setHotelTotalCount(hotelCount);
      setHotelPage(page);
      setLastHotelPayload(payload);
      setHotelFilters(HOTEL_FILTERS_INITIAL_VALUES);
      setHasSearchedHotels(true);
      setSaveHotelModalOpen(false);
      setHotelToSave(null);
      if (formValues) {
        setLastHotelSearchContext({
          query: formValues.location.trim(),
          checkInDate: formValues.checkIn,
          checkOutDate: formValues.checkOut,
          guests: formValues.guests,
        });
      }
    } catch (error: any) {
      console.error('Hotel search error:', error);
      if (activeTabRef.current !== tabWhenStarted) return;
      setHotelSearchResults([]);
      setHotelTotalCount(0);
      setHotelPage(1);
      setLastHotelPayload(null);
      setHasSearchedHotels(true);
      setLastHotelSearchContext(null);
      setHotelSearchError(getApiErrorMessage(error, 'Hotel search failed. Please try again.'));
    }
  };

  const handleHotelSearchSubmit = async (formValues: HotelSearchFormData) => {
    setHotelSearchResults([]);
    setHotelFilteredResults([]);
    setHotelTotalCount(0);
    setHotelPage(1);
    const placeCity = formValues.location_details?.city?.trim();
    const placeCountryCode = formValues.location_details?.countryCode?.trim();
    const payload: HotelSearchPayload = {
      query: formValues.location.trim() || undefined,
      city: placeCity || undefined,
      country_code: placeCountryCode || undefined,
      check_in_date: formValues.checkIn,
      check_out_date: formValues.checkOut,
      adults: formValues.guests,
    };
    await runHotelSearch(payload, 1, formValues);
  };

  const handleHotelPageChange = (page: number) => {
    if (!lastHotelPayload || page === hotelPage) return;
    void runHotelSearch(lastHotelPayload, page);
  };

  const runPlaceSearch = async (
    payload: PlaceSearchPayload,
    page: number,
    options?: {
      formValues?: PlacesSearchFormData;
      searchFilters?: PlaceFiltersType;
      sortBy?: PlacesFormSort;
      isNewSearch?: boolean;
    }
  ) => {
    const tabWhenStarted = activeTabRef.current;
    const searchFilters = normalizePlaceFilters(options?.searchFilters ?? placeFilters);
    const sortBy = options?.sortBy ?? lastPlaceSortRef.current;
    setPlaceSearchError(null);
    closeSaveSuccessToast();
    if (options?.isNewSearch) {
      setPlaceSearchResults([]);
      setPlaceTotalCount(0);
      setPlacePage(1);
    }
    try {
      const result = await placeSearch({ payload, page, searchFilters, sortBy }).unwrap();
      const items = applyPlaceBooleanFilters(normalizePlaceSearchResponse(result), searchFilters);

      if (activeTabRef.current !== tabWhenStarted) return;

      setPlaceSearchResults(items);
      const placeCount = result.count ?? items.length;
      setPlaceTotalCount(placeCount);
      setPlacePage(page);
      setLastPlacePayload(payload);
      lastPlacePayloadRef.current = payload;
      appliedPlaceFiltersRef.current = searchFilters;
      lastPlaceSortRef.current = sortBy;
      if (options?.isNewSearch) {
        setPlaceFilters(PLACE_FILTERS_INITIAL_VALUES);
        appliedPlaceFiltersRef.current = PLACE_FILTERS_INITIAL_VALUES;
      }
      setHasSearchedPlaces(true);
      setSavePlaceModalOpen(false);
      setPlaceToSave(null);
      if (options?.formValues) {
        const formValues = options.formValues;
        setPlaceCategoryLabel(resolvePlaceTypeLabel(formValues.placeType, placeTypeOptionsForLabel));
        setLastPlaceSearchContext({
          locationLabel: formValues.location.trim(),
          placeType: formValues.placeType.trim(),
          placeTypeLabel: resolvePlaceTypeLabel(formValues.placeType, placeTypeOptionsForLabel),
          radiusMeters: payload.radius,
          rankLabel: resolveRankLabel(formValues.sortBy),
        });
      }
    } catch (error: any) {
      console.error('Place search error:', error);
      if (activeTabRef.current !== tabWhenStarted) return;
      setPlaceSearchResults([]);
      setPlaceTotalCount(0);
      setPlacePage(1);
      setLastPlacePayload(null);
      setHasSearchedPlaces(true);
      setLastPlaceSearchContext(null);
      setPlaceCategoryLabel('');
      setPlaceSearchError(getApiErrorMessage(error, 'Place search failed. Please try again.'));
    }
  };

  const handlePlaceSearchSubmit = async (formValues: PlacesSearchFormData) => {
    const city = formValues.location_details?.city?.trim();
    const countryCode = formValues.location_details?.countryCode?.trim();
    if (!city || !countryCode) {
      setPlaceSearchError('Pick a city from the suggestions.');
      return;
    }

    const placeTypeTrimmed = formValues.placeType.trim();
    if (!placeTypeTrimmed || placeTypeTrimmed.toLowerCase() === 'all') {
      setPlaceSearchError('Select a place type.');
      return;
    }

    const radius = radiusMetersFromDistanceField(formValues.distanceFromCenter);
    const payload: PlaceSearchPayload = {
      city,
      country_code: countryCode,
      radius,
      place_type: placeTypeTrimmed,
    };
    await runPlaceSearch(payload, 1, {
      formValues,
      searchFilters: PLACE_FILTERS_INITIAL_VALUES,
      sortBy: formValues.sortBy,
      isNewSearch: true,
    });
  };

  const handlePlaceFiltersChange = (next: PlaceFiltersType) => {
    const normalized = normalizePlaceFilters(next);
    const payload = lastPlacePayloadRef.current;
    const shouldRefetch =
      hasSearchedPlaces && payload != null && hasBackendPlaceFiltersChanged(appliedPlaceFiltersRef.current, normalized);

    setPlaceFilters(normalized);

    if (!shouldRefetch) return;

    appliedPlaceFiltersRef.current = normalized;
    void runPlaceSearch(payload, 1, {
      searchFilters: normalized,
      sortBy: lastPlaceSortRef.current,
    });
  };

  const handlePlacePageChange = (page: number) => {
    if (!lastPlacePayload || page === placePage) return;
    void runPlaceSearch(lastPlacePayload, page, {
      searchFilters: placeFilters,
      sortBy: lastPlaceSortRef.current,
    });
  };

  const displayedFlights = searchResults;

  useEffect(() => {
    if (hotelSearchResults.length === 0) {
      setHotelFilteredResults([]);
      return;
    }

    let result = [...hotelSearchResults];

    if (hotelFilters.ratings.length > 0) {
      result = result.filter(hotel => {
        if (hotel.rating == null) return false;
        const rounded = Math.round(hotel.rating);
        return hotelFilters.ratings.includes(rounded);
      });
    }

    const priceToNumber = (value: string | undefined): number | null => {
      if (!value) return null;
      const digits = value.replace(/[^\d.,-]/g, '').replace(/,/g, '');
      const n = Number(digits);
      return Number.isFinite(n) ? n : null;
    };

    switch (hotelFilters.sortBy) {
      case 'price_low':
        result.sort((a, b) => {
          const pa = priceToNumber(a.price) ?? Number.MAX_SAFE_INTEGER;
          const pb = priceToNumber(b.price) ?? Number.MAX_SAFE_INTEGER;
          return pa - pb;
        });
        break;
      case 'price_high':
        result.sort((a, b) => {
          const pa = priceToNumber(a.price) ?? Number.MIN_SAFE_INTEGER;
          const pb = priceToNumber(b.price) ?? Number.MIN_SAFE_INTEGER;
          return pb - pa;
        });
        break;
      case 'best_rating':
      default:
        result.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
        break;
    }

    setHotelFilteredResults(result);
  }, [hotelSearchResults, hotelFilters]);

  useEffect(() => {
    setMobileFiltersOpen(false);
  }, [activeTab]);

  useEffect(() => {
    if (!isMobile) {
      setMobileFiltersOpen(false);
    }
  }, [isMobile]);

  const handleMobileFiltersOpen = () => {
    if (isMobile) setMobileFiltersOpen(true);
  };

  const flightTotalPages = useMemo(() => getTotalPages(flightTotalCount), [flightTotalCount]);
  const hotelTotalPages = useMemo(() => getTotalPages(hotelTotalCount), [hotelTotalCount]);
  const placeTotalPages = useMemo(() => getTotalPages(placeTotalCount), [placeTotalCount]);

  const showFlightsWorkspace = activeTab === 'flights' && hasSearchedFlights;
  const showFlightResultsSection = showFlightsWorkspace && !searchError;
  const showFlightEmptyResults = showFlightResultsSection && !isFlightSearching && searchResults.length === 0;

  const showHotelsWorkspace = activeTab === 'hotels' && hasSearchedHotels;
  const showHotelEmptyResults = showHotelsWorkspace && hotelSearchResults.length === 0 && !hotelSearchError;
  const showHotelResultsList = showHotelsWorkspace && hotelSearchResults.length > 0;

  const showPlacesWorkspace = activeTab === 'places' && hasSearchedPlaces;
  const showPlacesResultsSection = showPlacesWorkspace && !placeSearchError;
  const showPlaceEmptyResults = showPlacesResultsSection && !isPlaceLoading && placeSearchResults.length === 0;

  return (
    <Box className={styles.discover}>
      {lastSearchMeta && (
        <SaveFlightModal
          open={saveModalOpen}
          onClose={() => {
            setSaveModalOpen(false);
            setFlightToSave(null);
          }}
          flight={flightToSave}
          searchContext={lastSearchMeta.context}
          travelersCount={lastSearchMeta.travelersCount}
          travelClass={lastSearchMeta.travelClass}
          onSaved={showSaveSuccessToast}
        />
      )}

      {lastHotelSearchContext && (
        <SaveHotelModal
          open={saveHotelModalOpen}
          onClose={() => {
            setSaveHotelModalOpen(false);
            setHotelToSave(null);
          }}
          hotel={hotelToSave}
          searchContext={lastHotelSearchContext}
        />
      )}

      {lastPlaceSearchContext && (
        <SavePlaceModal
          open={savePlaceModalOpen}
          onClose={() => {
            setSavePlaceModalOpen(false);
            setPlaceToSave(null);
          }}
          place={placeToSave}
          searchContext={lastPlaceSearchContext}
          onSaved={showSaveSuccessToast}
        />
      )}

      <Box className={styles.discoverHeader}>
        <Box className={styles.discoverHeaderLeft}>
          <Typography
            variant={isMobile ? 'h6Semibold' : 'h4Bold'}
            sx={{ color: '#222B3A' }}
          >
            Discover
          </Typography>
          <Typography
            variant={isMobile ? 'h11' : 'h10'}
            sx={{ color: '#5E6A81' }}
          >
            Find flights, hotels and places
          </Typography>
        </Box>
      </Box>

      <Box className={styles.tabsNavWrapper}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={handleTabChange}
          className={styles.toggleGroup}
        >
          <ToggleButton
            value="flights"
            className={`${activeTab === 'flights' && styles.selected} ${styles.toggleButton}`}
          >
            {activeTab === 'flights' ? (
              <Typography variant="h10Bold">Flights</Typography>
            ) : (
              <Typography variant="h10Semibold">Flights</Typography>
            )}
          </ToggleButton>
          {/* <ToggleButton
            value="hotels"
            className={`${activeTab === 'hotels' && styles.selected} ${styles.toggleButton}`}
          >
            {activeTab === 'hotels' ? (
              <Typography variant="h10Bold">Hotels</Typography>
            ) : (
              <Typography variant="h10Semibold">Hotels</Typography>
            )}
          </ToggleButton> */}
          <ToggleButton
            value="places"
            className={`${activeTab === 'places' && styles.selected} ${styles.toggleButton}`}
          >
            {activeTab === 'places' ? (
              <Typography variant="h10Bold">Places & To Do</Typography>
            ) : (
              <Typography variant="h10Semibold">Places & To Do</Typography>
            )}
          </ToggleButton>
          <ToggleButton
            value="discover"
            className={`${activeTab === 'discover' && styles.selected} ${styles.toggleButton}`}
          >
            {activeTab === 'discover' ? (
              <Typography variant="h10Bold">Discover</Typography>
            ) : (
              <Typography variant="h10Semibold">Discover</Typography>
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {activeTab === 'flights' ? (
        <Box className={styles.searchFormSection}>
          <DiscoverSearchPanel
            isMobile={isMobile}
            sectionTitle="Flights"
            cardTitle="Flights Search"
            onFilterClick={isMobile ? handleMobileFiltersOpen : undefined}
          >
            <FlightSearchForm
              onSubmit={handleSearchSubmit}
              isLoading={isFlightSearching}
              isMobileLayout={isMobile}
            />
          </DiscoverSearchPanel>
        </Box>
      ) : activeTab === 'hotels' ? (
        <Box className={styles.searchFormSection}>
          <DiscoverSearchPanel
            isMobile={isMobile}
            sectionTitle="Hotels"
            cardTitle="Hotels Search"
            onFilterClick={isMobile ? handleMobileFiltersOpen : undefined}
          >
            <HotelSearchForm
              onSubmit={handleHotelSearchSubmit}
              isLoading={isHotelLoading}
              isMobileLayout={isMobile}
            />
          </DiscoverSearchPanel>
        </Box>
      ) : activeTab === 'places' ? (
        <Box className={styles.searchFormSection}>
          <DiscoverSearchPanel
            isMobile={isMobile}
            sectionTitle="Places & To Do"
            cardTitle="Places Search"
            onFilterClick={isMobile ? handleMobileFiltersOpen : undefined}
          >
            <PlacesSearchForm
              onSubmit={handlePlaceSearchSubmit}
              isLoading={isPlaceLoading}
              isMobileLayout={isMobile}
            />
          </DiscoverSearchPanel>
        </Box>
      ) : (
        <DiscoverTab
          isMobile={isMobile}
          mobileFiltersOpen={mobileFiltersOpen}
          onMobileFiltersOpen={handleMobileFiltersOpen}
          onCloseMobileFilters={() => setMobileFiltersOpen(false)}
        />
      )}

      {activeTab === 'flights' && searchError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setSearchError(null)}
        >
          {searchError}
        </Alert>
      ) : null}

      {activeTab === 'hotels' && hotelSearchError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setHotelSearchError(null)}
        >
          {hotelSearchError}
        </Alert>
      ) : null}

      {activeTab === 'places' && placeSearchError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setPlaceSearchError(null)}
        >
          {placeSearchError}
        </Alert>
      ) : null}

      {showHotelEmptyResults ? (
        <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h10Semibold"
            sx={{ color: '#222B3A' }}
          >
            No hotels found
          </Typography>
          <Typography
            variant="h11"
            sx={{ color: '#5E6A81', mt: 1 }}
          >
            Try different dates, location, or fewer filters.
          </Typography>
        </Box>
      ) : null}

      {showFlightResultsSection ? (
        <Box className={`${styles.resultsSection} ${isMobile ? styles.resultsSectionMobile : ''}`}>
          {!isMobile ? (
            <Box className={styles.filtersColumn}>
              <Box className={styles.filtersPanel}>
                <FlightFilters
                  filters={filters}
                  onFiltersChange={handleFlightFiltersChange}
                  airlineOptions={airlineOptions}
                  airportOptions={airportOptions}
                />
              </Box>
            </Box>
          ) : null}

          <Box className={styles.resultsColumn}>
            {showFlightEmptyResults ? (
              <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                  variant="h10Semibold"
                  sx={{ color: '#222B3A' }}
                >
                  No flights found
                </Typography>
                <Typography
                  variant="h11"
                  sx={{ color: '#5E6A81', mt: 1 }}
                >
                  Try different dates, airports, or fewer filters.
                </Typography>
              </Box>
            ) : (
              <>
                {displayedFlights.map((flight, index) => (
                  <FlightResultCard
                    key={getFlightItemKey(flight, index)}
                    flight={flight}
                    isRoundTrip={Boolean(flight.return_departure_date_time && flight.return_arrival_date_time)}
                    onSave={f => {
                      if (!lastSearchMeta) return;
                      setFlightToSave(f);
                      setSaveModalOpen(true);
                    }}
                  />
                ))}

                <DiscoverPagination
                  page={flightPage}
                  totalPages={flightTotalPages}
                  onPageChange={handleFlightPageChange}
                  disabled={isFlightSearching}
                />
              </>
            )}
          </Box>
        </Box>
      ) : null}

      {showHotelResultsList ? (
        <Box className={`${styles.resultsSection} ${isMobile ? styles.resultsSectionMobile : ''}`}>
          {!isMobile ? (
            <Box className={styles.filtersColumn}>
              <Box className={styles.filtersPanel}>
                <HotelFilters
                  filters={hotelFilters}
                  onFiltersChange={setHotelFilters}
                />
              </Box>
            </Box>
          ) : null}

          <Box className={styles.resultsColumn}>
            {hotelFilteredResults.map((hotel, index) => (
              <HotelResultCard
                key={`${hotel.external_id || hotel.name}-${index}`}
                hotel={hotel}
                onSave={h => {
                  if (!lastHotelSearchContext) return;
                  setHotelToSave(h);
                  setSaveHotelModalOpen(true);
                }}
              />
            ))}

            <DiscoverPagination
              page={hotelPage}
              totalPages={hotelTotalPages}
              onPageChange={handleHotelPageChange}
              disabled={isHotelLoading}
            />
          </Box>
        </Box>
      ) : null}

      {showPlacesResultsSection ? (
        <Box className={`${styles.resultsSection} ${isMobile ? styles.resultsSectionMobile : ''}`}>
          {!isMobile ? (
            <Box className={styles.filtersColumn}>
              <Box className={styles.filtersPanel}>
                <PlaceFilters
                  filters={placeFilters}
                  onFiltersChange={handlePlaceFiltersChange}
                />
              </Box>
            </Box>
          ) : null}

          <Box className={styles.resultsColumn}>
            {showPlaceEmptyResults ? (
              <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                  variant="h10Semibold"
                  sx={{ color: '#222B3A' }}
                >
                  No places found
                </Typography>
                <Typography
                  variant="h11"
                  sx={{ color: '#5E6A81', mt: 1 }}
                >
                  Try a different location, type, or distance. You can also relax filters in the sidebar.
                </Typography>
              </Box>
            ) : (
              <>
                {placeSearchResults.map((place, index) => (
                  <PlaceResultCard
                    key={`${place.id || place.name}-${place.address || ''}-${index}`}
                    place={place}
                    categoryLabel={placeCategoryLabel}
                    onSave={p => {
                      if (!lastPlaceSearchContext) return;
                      setPlaceToSave(p);
                      setSavePlaceModalOpen(true);
                    }}
                  />
                ))}

                <DiscoverPagination
                  page={placePage}
                  totalPages={placeTotalPages}
                  onPageChange={handlePlacePageChange}
                  disabled={isPlaceLoading}
                />
              </>
            )}
          </Box>
        </Box>
      ) : null}

      {isMobile && mobileFiltersOpen && activeTab === 'flights' ? (
        <FlightFiltersModal
          open
          onClose={() => setMobileFiltersOpen(false)}
          filters={filters}
          onApply={handleFlightFiltersChange}
          airlineOptions={airlineOptions}
          airportOptions={airportOptions}
        />
      ) : null}

      {isMobile && mobileFiltersOpen && activeTab === 'hotels' ? (
        <HotelFiltersModal
          open
          onClose={() => setMobileFiltersOpen(false)}
          filters={hotelFilters}
          onApply={setHotelFilters}
        />
      ) : null}

      {isMobile && mobileFiltersOpen && activeTab === 'places' ? (
        <PlaceFiltersModal
          open
          onClose={() => setMobileFiltersOpen(false)}
          filters={placeFilters}
          onApply={handlePlaceFiltersChange}
        />
      ) : null}

      <Snackbar
        key={saveSuccessSnackbarKey}
        open={saveSuccessToastOpen}
        autoHideDuration={5000}
        onClose={closeSaveSuccessToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSaveSuccessToast}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {saveSuccessToastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Discover;
